import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { ADDRESS, API, EMAIL, IMAGES, PHONE, PHONE_LINK } from "@/data/site";
import PageHero from "@/components/PageHero";
import { Accent, Reveal, SectionHead } from "@/components/Reveal";

const CHANNELS = [
  { icon: Phone, label: "Call us 24×7", value: PHONE, href: PHONE_LINK, testid: "contact-phone" },
  { icon: Mail, label: "Write to us", value: EMAIL, href: `mailto:${EMAIL}`, testid: "contact-email" },
  { icon: MapPin, label: "Visit the studio", value: ADDRESS, href: null, testid: "contact-address" },
  { icon: Clock, label: "Support hours", value: "Always open — 24×7, 365 days", href: null, testid: "contact-hours" },
];

const SERVICES = ["India Tour", "Cab & Taxi", "International Tour", "Custom Honeymoon", "Corporate Travel", "General Enquiry"];

const Contact = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "General Enquiry", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const pkg = params.get("package");
    if (pkg) {
      setForm((f) => ({
        ...f,
        service: pkg.startsWith("intl") ? "International Tour" : "India Tour",
        message: `I'd like details about the "${pkg.replace(/-/g, " ")}" package.`,
      }));
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent — a travel expert will reach out within hours.");
      setForm({ name: "", email: "", phone: "", service: "General Enquiry", message: "" });
    } catch {
      toast.error("Could not send your enquiry. Please call us instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <PageHero
        image={IMAGES.cta}
        eyebrow="Contact & Booking"
        title={<>Say hello. We'll take it <Accent>from here</Accent></>}
      />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-3">
          <SectionHead
            eyebrow="Enquire"
            title={<>Tell us about your <Accent>trip</Accent></>}
          />
          <Reveal delay={0.12}>
            <form onSubmit={submit} data-testid="contact-form" className="glass rounded-3xl p-7 sm:p-10 mt-8 grid sm:grid-cols-2 gap-5">
              <input required placeholder="Full name" value={form.name} onChange={set("name")} data-testid="contact-name-input" className="input-dark" />
              <input required type="tel" placeholder="Phone number" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" className="input-dark" />
              <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} data-testid="contact-email-input" className="input-dark sm:col-span-2" />
              <select value={form.service} onChange={set("service")} data-testid="contact-service-select" className="input-dark sm:col-span-2">
                {SERVICES.map((s) => (
                  <option key={s} value={s} className="bg-[#121820]">{s}</option>
                ))}
              </select>
              <textarea
                required
                placeholder="Where do you want to go? When? How many travellers?"
                value={form.message}
                onChange={set("message")}
                rows={5}
                data-testid="contact-message-input"
                className="input-dark sm:col-span-2 resize-none"
              />
              <button type="submit" disabled={busy} data-testid="contact-submit-button" className="btn-gold sm:col-span-2 disabled:opacity-60">
                {busy ? "Sending…" : "Send Enquiry"}
              </button>
            </form>
          </Reveal>
        </div>

        <div className="lg:col-span-2 space-y-5">
          {CHANNELS.map((c, i) => (
            <Reveal key={c.label} delay={i * 0.08}>
              <div className="glass rounded-2xl p-6 flex items-start gap-4" data-testid={c.testid}>
                <span className="w-11 h-11 shrink-0 rounded-2xl bg-[#F2C230]/15 flex items-center justify-center text-[#F2C230]">
                  <c.icon size={18} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-[#F5F5F0]/50 font-semibold">{c.label}</p>
                  {c.href ? (
                    <a href={c.href} className="font-sora text-base font-semibold mt-1.5 block hover:text-[#F2C230] transition-colors">
                      {c.value}
                    </a>
                  ) : (
                    <p className="font-sora text-base font-semibold mt-1.5">{c.value}</p>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.3}>
            <div className="rounded-3xl overflow-hidden border border-white/10 h-64">
              <iframe
                title="Orego Tours — Kuzhivelippady, Ernakulam, Kerala"
                src="https://www.google.com/maps?q=KK+Buildings,+Edappally+Pukkattupady+Rd,+Kuzhivelippady,+Ernakulam,+Kerala+683561&output=embed"
                className="w-full h-full grayscale invert-[0.9] contrast-[0.9]"
                loading="lazy"
                data-testid="contact-map"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Contact;
